import React from 'react'
import IconDots from 'assets/dots.png'
import ArrowNext from 'assets/arrowNext.png'
import IconPencil from 'assets/pencil-01.png'
import { ButtonDropdown } from 'components/molecules/ButtonDropdown/ButtonDropdown'
import IconTrash from 'assets/trash.png'
import IconCopy from 'assets/copy.png'
import AppModalConfirmation from 'components/atoms/AppModalConfirmation/AppModalConfirmation'
import { useState } from 'react'
import { usePermissions } from 'hooks/usePermissions'
import { RolePermissions } from 'utils/constants'
import { OPERATION_MODE_CODES } from 'core/domain/operationModeConstants'
import { OperationMode } from 'core/domain/operationMode'
import { OperationModeBadge } from 'core/presentation/components/molecules/OperationModeBadge/OperationModeBadge'
import { TemplateStatus, isDraft, isDeactivated } from 'templates/domain/entities/TemplateStatus'
import { TemplateStatusBadge } from 'templates/presentation/components/molecules/TemplateStatusBadge'

interface Props {
    id: number
    title: string
    countRules: number
    countChutes: number
    nameWarehouse?: string
    operationMode?: OperationMode
    status?: TemplateStatus
    statusName?: string
    onSubmit?: () => void
    deletePattern: () => void
    editPattern: () => void
    duplicatePattern: () => void
}

export default function PatternCardItem({
    id,
    title,
    countRules,
    countChutes,
    onSubmit,
    deletePattern,
    editPattern,
    duplicatePattern,
    nameWarehouse,
    operationMode,
    status,
    statusName,
}: Props) {
    const [confirmModal, setConfirmModal] = useState(false)
    const { can } = usePermissions()

    const handleDeletePattern = () => {
        setConfirmModal(false)
        deletePattern()
    }

    const canEdit = can(RolePermissions.templates.edit)
    const canDelete = can(RolePermissions.templates.delete)
    const canDuplicate = can(RolePermissions.templates.duplicate)

    const highlightPendingChutes = isDraft(status) && countChutes === 0
    const isTemplateDeactivated = isDeactivated(status)
    const titleColor = isTemplateDeactivated ? 'text-[#94A3B8]' : 'text-[#141A1F]'
    const infoTextColor = isTemplateDeactivated ? 'text-[#94A3B8]' : 'text-[#3E5060]'
    const chutesTextColor = isTemplateDeactivated
        ? 'text-[#94A3B8]'
        : highlightPendingChutes
            ? 'text-[#CC6D02]'
            : 'text-[#3E5060]'

    const dropdownOptions = [
        ...(canEdit && !isTemplateDeactivated ? [{ title: 'Editar', icon: IconPencil, onClick: () => editPattern() }] : []),
        ...(canDuplicate && !isTemplateDeactivated ? [{ title: 'Duplicar', icon: IconCopy, onClick: () => duplicatePattern() }] : []),
        ...(canDelete ? [{ title: 'Eliminar', icon: IconTrash, onClick: () => setConfirmModal(true) }] : []),
    ]

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if ((e.key === 'Enter' || e.key === ' ') && !confirmModal && onSubmit) {
            e.preventDefault()
            onSubmit()
        }
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
                if (!confirmModal && onSubmit) {
                    onSubmit()
                } else {
                    e.stopPropagation()
                    setConfirmModal(false)
                }
            }}
            onKeyDown={handleKeyDown}
            aria-label={`Plantilla ${title}`}
            className="w-full border border-[#CBD5E1] hover:border-[#1F2531] cursor-pointer transition-colors duration-200 flex justify-between items-center px-4 py-4 bg-white rounded-xl mb-4"
        >
            <AppModalConfirmation
                title={'Eliminar Plantilla ' + id}
                description="¿Estás seguro de que deseas eliminar esta plantilla? Esta acción no se puede deshacer."
                onClose={() => setConfirmModal(false)}
                handleConfirm={handleDeletePattern}
                isOpen={confirmModal}
            />
            <div className="text-left">
                <div className="flex items-center">
                    <p className={`font-semibold text-base ${titleColor}`}>{title}</p>
                    <div className="ml-5" onClick={(e) => e.stopPropagation()}>
                        {dropdownOptions.length > 0 && (
                            <ButtonDropdown options={dropdownOptions} color="primary" imageSrc={IconDots} />
                        )}
                    </div>
                </div>
                <div className="flex items-center mt-1 gap-3">
                    <span className={`text-sm font-normal ${infoTextColor}`}>{countRules} reglas</span>
                    {operationMode?.code === OPERATION_MODE_CODES.SORTER && (
                        <>
                            <div className="w-px h-4 bg-gray-300" aria-hidden="true" />
                            <span className={`text-sm font-normal ${chutesTextColor}`}>{countChutes} chutes asignados</span>
                        </>
                    )}
                    {nameWarehouse && (
                        <>
                            <div className="w-px h-4 bg-gray-300" aria-hidden="true" />
                            <span className={`text-sm font-normal ${infoTextColor}`}>Warehouse: {nameWarehouse}</span>
                        </>
                    )}
                    {status && (
                        <>
                            <div className="w-px h-4 bg-gray-300" aria-hidden="true" />
                            <TemplateStatusBadge status={status} label={statusName} />
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-3">
                {operationMode && <OperationModeBadge operationMode={operationMode} variant="list" />}
                <img src={ArrowNext} alt="" aria-hidden="true" />
            </div>
        </div>
    )
}
